import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {Observable, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {readProfileRaw} from '@gatewee/services';
import {GenericCrudService} from '@gatewee/services/generic-crud.service';
import {
  isTwoFactorCancelled,
  isTwoFactorMaxAttempts,
} from '@gatewee/services/two-factor-auth.service';
import type {
  ApiKeyActions,
  CreateRecipientKeyPayload,
} from '@gatewee/components/v2';
import {RecipientKey} from '@gatewee/types/recipient';

@Component({
  selector: 'app-recipient-keys',
  templateUrl: './recipient-keys.component.html',
  styleUrls: ['./recipient-keys.component.css'],
  standalone: false,
})
export class RecipientKeysComponent implements OnInit {
  private crudService: GenericCrudService<RecipientKey>;

  keys: RecipientKey[] = [];
  page = 1;
  pageSize = 10;
  total = 0;
  from = 0;
  take = 10;

  breadcrumb = [
    {label: 'nav.breadcrumb.settings', path: '/panel'},
    {label: 'nav.menu.apiKeys'},
  ];

  apiKeyActions: ApiKeyActions = {
    onCreate: (payload: CreateRecipientKeyPayload) => {
      const recipientId = this.getRecipientId();
      if (recipientId === null) {
        this.alerts
          .open(this.translate.instant('features.apiKeys.errors.noRecipient'), {
            label: this.translate.instant('common.status.error'),
            appearance: 'error',
          })
          .subscribe();
        return throwError(() => new Error('No recipient context'));
      }
      const body = {
        ...payload,
        recipient: {id: recipientId},
      };
      return this.crudService
        .createWith2Fa(body as unknown as RecipientKey)
        .pipe(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          tap((key: RecipientKey & {secret?: string}) => {
            this.alerts
              .open(this.translate.instant('features.apiKeys.createdSuccess'), {
                label: this.translate.instant('common.status.success'),
                appearance: 'success',
              })
              .subscribe();
          }),
          catchError((err: unknown) => {
            if (isTwoFactorCancelled(err)) {
              return throwError(() => err);
            }
            if (isTwoFactorMaxAttempts(err)) {
              this.alerts
                .open(
                  this.translate.instant(
                    'common.twoFactor.maxAttemptsExceeded',
                  ),
                  {
                    label: this.translate.instant('common.status.error'),
                    appearance: 'error',
                  },
                )
                .subscribe();
              return throwError(() => err);
            }
            const httpErr = err as HttpErrorResponse;
            this.alerts
              .open(
                httpErr?.error?.message ??
                  this.translate.instant('common.status.error'),
                {
                  label: this.translate.instant('common.status.error'),
                  appearance: 'error',
                },
              )
              .subscribe();
            return throwError(() => err);
          }),
        ) as Observable<RecipientKey & {secret?: string}>;
    },
    onDisable: (id: number) =>
      this.crudService.disable(id).pipe(
        tap(() => {
          this.fetchKeys();
          this.alerts
            .open(this.translate.instant('features.apiKeys.disabledSuccess'), {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            })
            .subscribe();
        }),
        catchError((err: HttpErrorResponse) => {
          this.alerts
            .open(
              err?.error?.message ??
                this.translate.instant('common.status.error'),
              {
                label: this.translate.instant('common.status.error'),
                appearance: 'error',
              },
            )
            .subscribe();
          return throwError(() => err);
        }),
      ),
    loadKeys: () => this.fetchKeys(),
  };

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private alerts: TuiAlertService,
  ) {
    this.crudService = new GenericCrudService<RecipientKey>(
      '/recipients-keys/recipient',
      this.http,
    );
  }

  private getRecipientId(): number | null {
    try {
      const profile = JSON.parse(readProfileRaw());
      const id = profile?.recipient?.id;
      return id !== null ? Number(id) : null;
    } catch {
      return null;
    }
  }

  ngOnInit(): void {
    this.fetchKeys();
  }

  fetchKeys(): void {
    this.crudService.findAll().subscribe({
      next: (data: RecipientKey[]) => {
        this.keys = data;
        this.total = data.length;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error fetching keys:', error);
      },
    });
  }

  onPageChange(page: number): void {
    this.page = page;
    this.from = (page - 1) * this.take;
    this.fetchKeys();
  }
}
